import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import styled, { css } from "styled-components";
import useStore from "../../store.js";
import { lockDocumentScroll } from "../../utils/documentScrollLock.js";
import chatgptLogo from "../../img/chatgpt-logo.svg";
import claudeLogo from "../../img/claude-logo.svg";
import geminiLogo from "../../img/gemini-logo.svg";
import {
  PROMPT_PROVIDERS,
  purchasePromptExport,
  sendPromptEvent,
  copyPromptToClipboard,
} from "../../utils/promptExportApi.js";

const providerLogos = {
  chatgpt: chatgptLogo,
  claude: claudeLogo,
  gemini: geminiLogo,
};

/*
'내가 할 일 찾기' 03 결제 전 확인 / 04 복사 완료 시트.
- 결제 전 닫기(X·오버레이)는 과금 없이 읽던 화면으로 복귀한다.
- 완성 프롬프트는 컴포넌트 상태로만 다루고 저장하지 않는다.
*/
const PromptExportSheet = ({ isOpen, onClose, logId, purchased, onPurchased, onInsufficientCredit }) => {
  const { userInfo, updateUserInfo } = useStore();
  const [step, setStep] = useState(purchased ? "done" : "confirm");
  const [prompt, setPrompt] = useState("");
  const [sessionId, setSessionId] = useState("");
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const [helpful, setHelpful] = useState(null);
  const [reasonSent, setReasonSent] = useState(false);
  const [error, setError] = useState("");
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  useLayoutEffect(() => {
    if (!isOpen) return undefined;
    return lockDocumentScroll();
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || !purchased || prompt) return undefined;
    let cancelled = false;
    setBusy(true);
    // 완료 도크 재진입: 기존 구매를 무과금 재조립으로 불러온다.
    purchasePromptExport(logId)
        .then(({ status, data }) => {
          if (cancelled) return;
          if ((status === 200 || status === 201) && typeof data.prompt === "string" && data.prompt.trim()) {
            setPrompt(data.prompt);
            setSessionId(data.prompt_session_id || "");
          } else {
            setError("질문을 불러오지 못했어요. 다시 복사를 눌러주세요.");
          }
        })
        .catch(() => { if (!cancelled) setError("질문을 불러오지 못했어요. 다시 복사를 눌러주세요."); })
        .finally(() => { if (!cancelled) setBusy(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, purchased, logId]);

  if (!isOpen) return null;

  const eventBase = {
    search_log_id: Number(logId),
    prompt_session_id: sessionId || undefined,
  };

  const doCopy = async (text, copySessionId = sessionId) => {
    const ok = await copyPromptToClipboard(text);
    if (!mounted.current) return false;
    if (ok) {
      setCopied(true);
      setError("");
      sendPromptEvent({ ...eventBase, prompt_session_id: copySessionId || undefined, event_name: "ai_prompt_copy_succeeded" });
    } else {
      setCopied(false);
      setError("복사하지 못했어요. 다시 복사를 눌러 브라우저의 복사 권한을 허용해주세요.");
      sendPromptEvent({
        ...eventBase,
        prompt_session_id: copySessionId || undefined,
        event_name: "ai_prompt_step_failed",
        step: "copy",
        error_code: "clipboard_write_failed",
      });
    }
    return ok;
  };

  const handleConfirm = async () => {
    if (busy) return;
    sendPromptEvent({ ...eventBase, event_name: "ai_prompt_purchase_started" });
    if (userInfo && userInfo.creditAmount < 10) {
      onInsufficientCredit();
      return;
    }
    setBusy(true);
    setError("");
    try {
      const { status, data } = await purchasePromptExport(logId);
      if (!mounted.current) return;
      if (status === 201 || status === 200) {
        if (typeof data.prompt !== "string" || !data.prompt.trim()) {
          setError("질문을 불러오지 못했어요. 다시 시도해주세요.");
          return;
        }
        setPrompt(data.prompt);
        setSessionId(data.prompt_session_id || "");
        setStep("done");
        onPurchased?.();
        await doCopy(data.prompt, data.prompt_session_id);
        try {
          Promise.resolve(updateUserInfo(() => {})).catch(() => {});
        } catch (e) {
          /* 잔액 갱신 실패는 흐름을 막지 않음 */
        }
      } else if (status === 402) {
        onInsufficientCredit();
        sendPromptEvent({
          ...eventBase,
          event_name: "ai_prompt_step_failed",
          step: "payment",
          error_code: "insufficient_credit",
        });
      } else {
        setError("질문을 불러오지 못했어요. 잠시 후 다시 시도해주세요.");
      }
    } catch (e) {
      if (!mounted.current) return;
      sendPromptEvent({
        ...eventBase,
        event_name: "ai_prompt_step_failed",
        step: "payment",
        error_code: "network_error",
      });
      setError("질문을 불러오지 못했어요. 연결을 확인하고 다시 시도해주세요.");
    } finally {
      if (mounted.current) setBusy(false);
    }
  };

  const handleRecopy = async () => {
    if (busy) return;
    setBusy(true);
    setError("");
    try {
      let text = prompt;
      let copySessionId = sessionId;
      if (!text) {
        const { status, data } = await purchasePromptExport(logId);
        if (!mounted.current) return;
        if ((status === 200 || status === 201) && typeof data.prompt === "string" && data.prompt.trim()) {
          text = data.prompt;
          setPrompt(text);
          copySessionId = data.prompt_session_id || "";
          setSessionId(copySessionId);
        }
      }
      if (text) await doCopy(text, copySessionId);
      else setError("질문을 불러오지 못했어요. 다시 복사를 눌러주세요.");
    } catch (e) {
      if (mounted.current) setError("질문을 불러오지 못했어요. 연결을 확인하고 다시 복사를 눌러주세요.");
    } finally {
      if (mounted.current) setBusy(false);
    }
  };

  const handleProvider = (provider) => {
    sendPromptEvent({
      ...eventBase,
      event_name: "ai_prompt_provider_opened",
      provider: provider.key,
    });
    // 이동은 브라우저의 일반 링크가 즉시 처리한다. 계측 응답을 기다리지 않는다.
  };

  const handleHelpful = (value) => {
    if (helpful !== null) return;
    setHelpful(value);
    sendPromptEvent({
      ...eventBase,
      event_name: "ai_prompt_helpfulness_submitted",
      helpful: value,
    });
  };

  const handleReason = (reason) => {
    if (reasonSent) return;
    setReasonSent(true);
    sendPromptEvent({
      ...eventBase,
      event_name: "ai_prompt_relevance_issue_submitted",
      reason,
    });
  };

  return (
    <>
      <Overlay onClick={onClose} data-testid="prompt-export-overlay" />
      <Sheet role="dialog" aria-modal="true" data-testid="prompt-export-sheet">
        <Handle />
        <CloseButton onClick={onClose} aria-label="닫기">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path d="M2 2L14 14M14 2L2 14" stroke="rgba(0,0,0,0.4)" strokeWidth="1.5" strokeLinecap="round" />
          </svg>
        </CloseButton>
        {error && <ErrorNotice role="alert">{error}</ErrorNotice>}

        {step === "confirm" ? (
          <>
            <Title>
              판례는 찾았어요.
              <br />
              이제 내가 어떻게 해야 할지
              <br />
              알아볼 차례예요.
            </Title>
            <SubTitle>
              판례 5건과 내 상황으로, ChatGPT·Claude·Gemini에 붙여넣을 질문을 준비해요.
            </SubTitle>
            <ChecklistCard>
              {[
                ["판례 5건", "판례 내용·결론"],
                ["내 상황", "질문 원문"],
                ["대응 계획", "할 일·준비 자료·메시지 초안"],
              ].map(([label, meta], i) => (
                <ChecklistRow key={label} $last={i === 2}>
                  <CheckIcon />
                  <RowLabel>{label}</RowLabel>
                  <RowMeta>{meta}</RowMeta>
                </ChecklistRow>
              ))}
            </ChecklistCard>
            <PrivacyRow>
              <span>내 상황에 이름·전화번호가 있다면 지우고 진행하세요</span>
            </PrivacyRow>
            <GradientCta onClick={handleConfirm} disabled={busy} data-testid="prompt-export-cta">
              무엇을 하면 될지 묻기
              <PriceChip>10로우</PriceChip>
            </GradientCta>
            <FinePrint>결제 전에는 과금되지 않아요 · 질문은 외부 서비스로 자동 전송되지 않아요</FinePrint>
          </>
        ) : (
          <>
            <DoneIcon>
              <svg width="56" height="56" viewBox="0 0 56 56" fill="none">
                <defs>
                  <linearGradient id="peDone" x1="0" y1="0" x2="56" y2="56">
                    <stop offset="0.3" stopColor="#00F370" />
                    <stop offset="1" stopColor="#0057FF" />
                  </linearGradient>
                </defs>
                <circle cx="28" cy="28" r="26" stroke="url(#peDone)" strokeWidth="2" />
                <path d="M18 29L25 36L38 21" stroke="url(#peDone)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </DoneIcon>
            <DoneTitle>{busy ? "질문을 준비하고 있어요." : copied ? "질문이 복사됐어요." : "질문을 먼저 복사해주세요."}</DoneTitle>
            <DoneSub>{copied ? "AI를 열고 입력창에 붙여넣은 뒤 전송하세요." : "아래 복사 버튼을 누른 다음 AI를 여세요."}</DoneSub>
            <IncludeChip>내 상황 + 판례 5건 + 구체적인 대응 계획 요청</IncludeChip>
            <ProviderList>
              {PROMPT_PROVIDERS.map((provider) => (
                <ProviderRow
                  key={provider.key}
                  href={copied && !busy ? provider.url : undefined}
                  referrerPolicy="no-referrer"
                  aria-disabled={!copied || busy}
                  data-testid={`prompt-provider-${provider.key}`}
                  onClick={(event) => {
                    if (!copied || busy) { event.preventDefault(); return; }
                    handleProvider(provider);
                  }}
                >
                  <ProviderIcon>
                    <img src={providerLogos[provider.key]} alt={provider.label} />
                  </ProviderIcon>
                  <ProviderName>{provider.label}</ProviderName>
                  <OpenLabel>
                    바로 열기
                    <svg width="6" height="10" viewBox="0 0 6 10" fill="none">
                      <path d="M1 1L5 5L1 9" stroke="rgba(0,0,0,0.35)" strokeWidth="1.3" strokeLinecap="round" />
                    </svg>
                  </OpenLabel>
                </ProviderRow>
              ))}
            </ProviderList>
            <RecopyButton onClick={handleRecopy} disabled={busy} data-testid="prompt-export-recopy">
              {busy ? "불러오는 중…" : "다시 복사 · 추가 과금 없음"}
            </RecopyButton>
            <HelpfulRow>
              {helpful === null ? (
                <>
                  <span>도움이 됐나요?</span>
                  <HelpfulButton onClick={() => handleHelpful(true)}>도움됨</HelpfulButton>
                  <HelpfulButton onClick={() => handleHelpful(false)}>도움안됨</HelpfulButton>
                </>
              ) : helpful === false && !reasonSent ? (
                <>
                  <span>어떤 점이 아쉬웠나요?</span>
                  <HelpfulButton onClick={() => handleReason("cases_do_not_match")}>판례 불일치</HelpfulButton>
                  <HelpfulButton onClick={() => handleReason("too_generic")}>너무 일반적</HelpfulButton>
                  <HelpfulButton onClick={() => handleReason("other")}>기타</HelpfulButton>
                </>
              ) : (
                <span>의견 감사합니다.</span>
              )}
            </HelpfulRow>
            <FinePrint>현재 탭에서 열려요 · 붙여넣기와 전송은 직접 해주세요</FinePrint>
          </>
        )}
      </Sheet>
    </>
  );
};

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.4);
  z-index: 150;
`;

const sheetBase = css`
  position: fixed;
  z-index: 151;
  background: #fff;
  box-sizing: border-box;
`;

const Sheet = styled.div`
  ${sheetBase}
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  width: 620px;
  max-width: calc(100vw - 32px);
  max-height: calc(100vh - 48px);
  max-height: calc(100dvh - 48px);
  overflow-y: auto;
  overscroll-behavior-y: contain;
  -webkit-overflow-scrolling: touch;
  border-radius: 24px;
  padding: 32px 32px 28px;
  box-shadow: 0 16px 48px rgba(0, 0, 0, 0.16);

  @media (max-width: 480px) {
    left: 0;
    top: auto;
    bottom: 0;
    transform: none;
    width: 100%;
    max-width: none;
    border-radius: 24px 24px 0 0;
    padding: 12px 20px calc(28px + env(safe-area-inset-bottom, 0px));
    box-shadow: 0 -8px 32px rgba(0, 0, 0, 0.12);
  }
`;

const Handle = styled.div`
  display: none;
  @media (max-width: 480px) {
    display: block;
    width: 40px;
    height: 4px;
    border-radius: 2px;
    background: #f1f1f1;
    margin: 0 auto 8px;
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 8px;
  right: 8px;
  width: 44px;
  height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  cursor: pointer;
`;

const Title = styled.div`
  margin-top: 12px;
  color: #1d1d1f;
  font-size: 19px;
  font-weight: 700;
  line-height: 28px;
  letter-spacing: -0.38px;
`;

const SubTitle = styled.div`
  margin-top: 8px;
  color: #595959;
  font-size: 14px;
  line-height: 22px;
  letter-spacing: -0.28px;
`;

const ChecklistCard = styled.div`
  margin-top: 18px;
  padding: 6px 16px;
  border-radius: 16px;
  border: 1.5px solid transparent;
  background-image: linear-gradient(#fff, #fff),
    linear-gradient(90deg, #1ae276 33.51%, #3a7dff 86.88%);
  background-origin: border-box;
  background-clip: padding-box, border-box;
`;

const ChecklistRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 0;
  border-bottom: ${({ $last }) => ($last ? "none" : "1px solid #f5f5f5")};
`;

const CheckIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
    <defs>
      <linearGradient id="peCheck" x1="0" y1="0" x2="20" y2="20">
        <stop offset="0.3" stopColor="#00F370" />
        <stop offset="1" stopColor="#0057FF" />
      </linearGradient>
    </defs>
    <circle cx="10" cy="10" r="9" stroke="url(#peCheck)" strokeWidth="1.5" />
    <path d="M6 10.5L8.8 13L14 7.5" stroke="url(#peCheck)" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const RowLabel = styled.div`
  flex: 1;
  color: rgba(0, 0, 0, 0.8);
  font-size: 15px;
  font-weight: 600;
  letter-spacing: -0.3px;
`;

const RowMeta = styled.div`
  color: #595959;
  font-size: 12px;
  font-weight: 500;
  letter-spacing: -0.24px;
`;

const PrivacyRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 12px;
  padding: 12px 16px;
  border-radius: 12px;
  background: linear-gradient(107deg, rgba(0, 243, 112, 0.06) 8.55%, rgba(0, 87, 255, 0.06) 73.74%);
  color: rgba(0, 0, 0, 0.6);
  font-size: 13px;
  line-height: 19px;
  letter-spacing: -0.26px;
`;

const GradientCta = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  width: 100%;
  margin-top: 16px;
  height: 52px;
  border: none;
  border-radius: 26px;
  background: linear-gradient(92deg, #00f370 0%, #00badc 50.5%, #0057ff 100%);
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  color: #1d1d1f;
  font-size: 16px;
  font-weight: 700;
  letter-spacing: -0.32px;
  cursor: pointer;
  &:disabled {
    opacity: 0.6;
    cursor: default;
  }
`;

const PriceChip = styled.span`
  display: inline-flex;
  align-items: center;
  height: 28px;
  padding: 0 12px;
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.24);
  color: #1d1d1f;
  font-size: 13px;
  font-weight: 600;
  letter-spacing: -0.26px;
`;

const FinePrint = styled.div`
  margin-top: 12px;
  text-align: center;
  color: #595959;
  font-size: 12px;
  line-height: 18px;
  letter-spacing: -0.24px;
`;

const DoneIcon = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 16px;
`;

const DoneTitle = styled.div`
  margin-top: 14px;
  text-align: center;
  color: #1d1d1f;
  font-size: 19px;
  font-weight: 700;
  line-height: 28px;
  letter-spacing: -0.38px;
`;

const DoneSub = styled.div`
  margin-top: 6px;
  text-align: center;
  color: #595959;
  font-size: 14px;
  line-height: 22px;
  letter-spacing: -0.28px;
`;

const IncludeChip = styled.div`
  margin: 12px auto 0;
  width: fit-content;
  display: flex;
  align-items: center;
  height: 30px;
  padding: 0 14px;
  border-radius: 15px;
  background: linear-gradient(107deg, rgba(0, 243, 112, 0.06) 8.55%, rgba(0, 87, 255, 0.06) 73.74%);
  color: rgba(0, 0, 0, 0.55);
  font-size: 12px;
  font-weight: 500;
  letter-spacing: -0.24px;
`;

const ProviderList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: 18px;
`;

const ProviderRow = styled.a`
  display: flex;
  align-items: center;
  gap: 12px;
  height: 56px;
  padding: 0 16px;
  border-radius: 16px;
  border: 1px solid #f1f1f1;
  background: #fff;
  cursor: pointer;
  text-align: left;
  text-decoration: none;
  box-sizing: border-box;
  &[aria-disabled="true"] {
    opacity: 0.5;
    cursor: default;
  }
`;

const ErrorNotice = styled.p`
  margin: 24px 0 12px;
  color: #a32929;
  font-size: 13px;
  line-height: 20px;
`;

const ProviderIcon = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: 1px solid #f1f1f1;
  background: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  img {
    width: 18px;
    height: 18px;
  }
`;

const ProviderName = styled.div`
  flex: 1;
  color: rgba(0, 0, 0, 0.8);
  font-size: 15px;
  font-weight: 600;
  letter-spacing: -0.3px;
`;

const OpenLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  color: #595959;
  font-size: 13px;
  font-weight: 500;
  letter-spacing: -0.26px;
`;

const RecopyButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  margin-top: 14px;
  height: 48px;
  border-radius: 24px;
  border: 1px solid transparent;
  background-image: linear-gradient(#fff, #fff),
    linear-gradient(90deg, #1ae276 33.51%, #3a7dff 86.88%);
  background-origin: border-box;
  background-clip: padding-box, border-box;
  color: rgba(0, 0, 0, 0.6);
  font-size: 14px;
  font-weight: 500;
  letter-spacing: -0.28px;
  cursor: pointer;
  &:disabled {
    opacity: 0.6;
    cursor: default;
  }
`;

const HelpfulRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin-top: 14px;
  color: #595959;
  font-size: 13px;
  letter-spacing: -0.26px;
`;

const HelpfulButton = styled.button`
  height: 32px;
  padding: 0 12px;
  border-radius: 16px;
  border: 1px solid #f1f1f1;
  background: #fff;
  color: rgba(0, 0, 0, 0.6);
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
`;

export default PromptExportSheet;
